import React, { useState, useEffect, useRef } from "react";
import { loadYandexMaps } from "../../utils/maps";

export const TaskMap = ({ tasks, userLocation, onMapReady, radius = 10 }) => {
  const mapRef = React.useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Функция для расчёта уровня zoom на основе радиуса
  const getZoomForRadius = (radiusKm) => {
    // Примерное соответствие радиуса и zoom для Яндекс Карт
    // Чем больше zoom, тем ближе масштаб
    if (radiusKm <= 10) return 12;
    if (radiusKm <= 25) return 11;
    if (radiusKm <= 50) return 10;
    if (radiusKm <= 100) return 9;
    if (radiusKm <= 250) return 8;
    return 7;
  };

  useEffect(() => {
    if (!isInitialized) {
      initMap();
    } else if (mapInstance) {
      updateMapMarkers();
    }
  }, [tasks, userLocation, isInitialized]);

  // Обновляем zoom при изменении радиуса
  useEffect(() => {
    if (mapInstance && radius) {
      const newZoom = getZoomForRadius(radius);
      mapInstance.setZoom(newZoom, { duration: 300 });
    }
  }, [radius, mapInstance]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mapInstance) {
        mapInstance.destroy();
      }
    };
  }, [mapInstance]);

  const initMap = async () => {
    if (!mapRef.current || isInitialized) return;
    
    try {
      const ymaps = await loadYandexMaps();
      
      // Clear any existing map content
      if (mapRef.current) {
        mapRef.current.innerHTML = '';
      }
      
      const initialZoom = getZoomForRadius(radius);
      
      // Определяем центр карты: геолокация → первое задание с координатами → центр России
      let center;
      if (userLocation) {
        center = [userLocation.latitude, userLocation.longitude];
      } else if (tasks && tasks.length > 0) {
        const firstWithCoords = tasks.find(t => t.latitude && t.longitude);
        center = firstWithCoords
          ? [firstWithCoords.latitude, firstWithCoords.longitude]
          : [55.7558, 37.6173]; // Москва по умолчанию
      } else {
        center = [55.7558, 37.6173]; // Москва по умолчанию
      }
      
      const map = new ymaps.Map(mapRef.current, {
        center,
        zoom: userLocation ? initialZoom : 5,
        controls: ['zoomControl', 'fullscreenControl']
      });

      setMapInstance(map);
      setIsInitialized(true);
      
      // Pass map instance back to parent if callback provided
      if (onMapReady) {
        onMapReady(map);
      }
      
      // Add markers after map is created
      addMapMarkers(map, ymaps);
      
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  };

  const addMapMarkers = (map, ymaps) => {
    // Clear existing markers
    map.geoObjects.removeAll();
    
    // Add user location marker only if location available
    if (userLocation) {
      const userPlacemark = new ymaps.Placemark(
        [userLocation.latitude, userLocation.longitude],
        {
          balloonContent: 'Вы здесь',
          iconCaption: 'Вы'
        },
        {
          preset: 'islands#blueCircleDotIcon'
        }
      );
      map.geoObjects.add(userPlacemark);
    }

    // Add task markers (TZ10-1: удалённые задания без координат на карте не рендерятся)
    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    tasks.filter(t => t.latitude != null && t.longitude != null).forEach(task => {
      const placemark = new ymaps.Placemark(
        [task.latitude, task.longitude],
        {
          balloonContent: `
            <div>
              <h3>${esc(task.title)}</h3>
              <p><strong>Цена:</strong> ${esc(task.price)} ₽</p>
              <p><strong>Описание:</strong> ${esc(task.description)}</p>
              ${task.distance != null ? `<p><strong>Расстояние:</strong> ${esc(task.distance)} км</p>` : ''}
            </div>
          `,
          iconCaption: `${task.price} ₽`
        },
        {
          preset: 'islands#redIcon'
        }
      );
      map.geoObjects.add(placemark);
    });
  };

  const updateMapMarkers = async () => {
    if (!mapInstance) return;
    
    try {
      const ymaps = await loadYandexMaps();
      addMapMarkers(mapInstance, ymaps);
    } catch (error) {
      console.error('Error updating map markers:', error);
    }
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Карта заданий</h3>
      <div 
        ref={mapRef}
        style={{ width: '100%', height: '400px' }}
        className="rounded-lg border"
        id="yandex-map"
      />
    </div>
  );
};
